import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { leadsApi } from '../api'
import { Lead, LeadStage, STAGE_LABELS, STAGE_COLORS, PRIORITY_COLORS } from '../types'

const STAGES: LeadStage[] = [
  'new_lead',
  'contacted',
  'interested',
  'demo_scheduled',
  'demo_completed',
  'negotiation',
  'won',
  'lost',
]

export default function Pipeline() {
  const [leadsByStage, setLeadsByStage] = useState<Record<LeadStage, Lead[]>>({
    new_lead: [],
    contacted: [],
    interested: [],
    demo_scheduled: [],
    demo_completed: [],
    negotiation: [],
    won: [],
    lost: [],
  })
  const [loading, setLoading] = useState(true)
  const [draggedLead, setDraggedLead] = useState<Lead | null>(null)
  const [expandedStage, setExpandedStage] = useState<LeadStage | null>(null)

  useEffect(() => {
    loadPipeline()
  }, [])

  async function loadPipeline() {
    try {
      setLoading(true)
      const leads = await leadsApi.list()
      
      const grouped: Record<LeadStage, Lead[]> = {
        new_lead: [],
        contacted: [],
        interested: [],
        demo_scheduled: [],
        demo_completed: [],
        negotiation: [],
        won: [],
        lost: [],
      }
      
      leads.forEach((lead) => {
        grouped[lead.stage].push(lead)
      })
      
      setLeadsByStage(grouped)
    } catch (error) {
      console.error('Failed to load pipeline:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleDragStart(lead: Lead) {
    setDraggedLead(lead)
  }

  async function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
  }

  async function handleDrop(e: React.DragEvent, targetStage: LeadStage) {
    e.preventDefault()
    if (!draggedLead || draggedLead.stage === targetStage) return
    
    try {
      await leadsApi.updateStage(draggedLead.id, targetStage)
      await loadPipeline()
    } catch (error) {
      console.error('Failed to update lead stage:', error)
    }
    
    setDraggedLead(null)
  }

  async function handleMoveStage(leadId: number, targetStage: LeadStage) {
    try {
      await leadsApi.updateStage(leadId, targetStage)
      await loadPipeline()
    } catch (error) {
      console.error('Failed to update lead stage:', error)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Loading pipeline...</div>
  }

  return (
    <div className="pb-20 lg:pb-0">
      <h1 className="text-xl lg:text-2xl font-bold mb-4 lg:mb-6">Sales Pipeline</h1>
      
      {/* Desktop Kanban */}
      <div className="hidden lg:flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((stage) => (
          <div
            key={stage}
            className="flex-shrink-0 w-72"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage)}
          >
            <div className="bg-gray-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-700">{STAGE_LABELS[stage]}</h3>
                <span className="bg-gray-200 text-gray-600 px-2 py-1 rounded-full text-sm font-medium">
                  {leadsByStage[stage].length}
                </span>
              </div>
              
              <div className="space-y-3 min-h-[200px]">
                {leadsByStage[stage].map((lead) => (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={() => handleDragStart(lead)}
                    className={`bg-white rounded-lg p-4 shadow-sm border border-gray-200 cursor-move hover:shadow-md transition-shadow ${
                      draggedLead?.id === lead.id ? 'opacity-50' : ''
                    }`}
                  >
                    <Link to={`/leads/${lead.id}`} className="block">
                      <h4 className="font-medium text-gray-900 hover:text-primary-600">
                        {lead.coaching_name}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">{lead.owner_name}</p>
                      
                      <div className="flex items-center gap-2 mt-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[lead.priority]}`}>
                          {lead.priority}
                        </span>
                        {lead.student_count > 0 && (
                          <span className="text-xs text-gray-500">
                            {lead.student_count} students
                          </span>
                        )}
                      </div>
                      
                      {lead.next_followup && (
                        <div className="mt-2 text-xs text-orange-600">
                          📅 {new Date(lead.next_followup).toLocaleDateString()}
                        </div>
                      )}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Pipeline */}
      <div className="lg:hidden space-y-3">
        {STAGES.map((stage) => {
          const leads = leadsByStage[stage]
          const isExpanded = expandedStage === stage
          
          return (
            <div key={stage} className="card">
              <button
                onClick={() => setExpandedStage(isExpanded ? null : stage)}
                className="w-full flex justify-between items-center"
              >
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-700">{STAGE_LABELS[stage]}</h3>
                  <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs font-medium">
                    {leads.length}
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isExpanded && (
                <div className="mt-3 space-y-2">
                  {leads.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">No leads in this stage</p>
                  ) : (
                    leads.map((lead) => (
                      <div key={lead.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-1">
                          <Link to={`/leads/${lead.id}`} className="font-medium text-primary-600 text-sm">
                            {lead.coaching_name}
                          </Link>
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${PRIORITY_COLORS[lead.priority]}`}>
                            {lead.priority}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{lead.owner_name}</p>
                        
                        {/* Stage navigation buttons */}
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {STAGES.filter(s => s !== stage && s !== 'won' && s !== 'lost').slice(0, 3).map((targetStage) => (
                            <button
                              key={targetStage}
                              onClick={() => handleMoveStage(lead.id, targetStage)}
                              className="text-xs px-2 py-1 bg-white border border-gray-200 rounded hover:bg-primary-50 hover:border-primary-300"
                            >
                              → {STAGE_LABELS[targetStage].split(' ')[0]}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
